import { logError } from '../../shared/utils/logger';
import {
    getTokens as readTokens,
    setTokens as persistTokens,
    clearTokens,
} from '../../shared/utils/secureTokenStorage';

// Fallback API for when no explicit API base is configured
const FALLBACK_API_BASE = "https://api.cloudmortgage.io";

// Prefer same-origin (proxy) by default; allow explicit override via REACT_APP_API_BASE.
// In development, force relative path ("") to leverage the package.json proxy and avoid CORS.
// In production, use the environment variable if set, or fallback for known domains.
const getBaseUrl = () => {
    if (process.env.NODE_ENV !== 'production') {
        return ""; // Use proxy in development
    }

    // If explicit API base is set, use it
    if (process.env.REACT_APP_API_BASE) {
        return process.env.REACT_APP_API_BASE.replace(/\/+$/, "");
    }

    // Auto-detect and use fallback for Amplify preview URLs and production domains
    if (typeof window !== "undefined") {
        const origin = window.location?.origin || "";
        const isAmplifyPreview = origin.includes(".amplifyapp.com");
        const isProductionDomain = origin === "https://cloudrealtor.io" || origin === "https://cloudmortgage.io";

        if (isAmplifyPreview || isProductionDomain) {
            return FALLBACK_API_BASE;
        }
    }

    return "";
};

export const BASE_URL = getBaseUrl();

// Global authentication error handler
let globalAuthErrorHandler = null;

/**
 * Register a global callback to handle authentication errors
 * This will be called when authentication permanently fails (e.g., token refresh fails)
 */
export const setGlobalAuthErrorHandler = (handler) => {
    globalAuthErrorHandler = handler;
};

/**
 * Clear the global authentication error handler
 */
export const clearGlobalAuthErrorHandler = () => {
    globalAuthErrorHandler = null;
};


export const getStoredTokens = () => readTokens();

// Backward compatibility helper
export const normalizeTokens = (tokenData) => {
    // Handle different backend response formats
    if (!tokenData) return null;

    // If tokenData is a string, assume it's the access token
    if (typeof tokenData === "string") {
        return { accessToken: tokenData };
    }

    // If tokenData already has accessToken, use as-is
    if (tokenData.accessToken) {
        return tokenData;
    }

    // If tokenData has 'token' property (nested structure)
    if (tokenData.token) {
        if (typeof tokenData.token === "string") {
            return { accessToken: tokenData.token, refreshToken: tokenData.refreshToken };
        }
        return tokenData.token;
    }

    // Fallback: assume the object itself contains the token data
    return tokenData;
};

export async function refreshAccessToken() {
    const tokens = readTokens();
    if (!tokens?.refreshToken) {
        clearTokens();
        throw new Error("Authentication expired. Please sign in again.");
    }
    const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: tokens.refreshToken })
    });
    if (!response.ok) {
        clearTokens();
        throw new Error("Authentication expired. Please sign in again.");
    }
    const data = await response.json();
    if (data?.token) {
        // SECURITY: Preserve storage location (session vs local) during refresh
        const normalized = normalizeTokens(data.token);
        persistTokens(normalized, false);
        return normalized;
    }
    throw new Error("Authentication expired. Please sign in again.");
}

export const getAuthHeaders = (extraHeaders = {}) => {
    const tokens = readTokens();
    const headers = { ...extraHeaders };

    if (tokens?.accessToken) {
        headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return headers;
};

export async function fetchWithAuth(url, options = {}, retry = true) {
    const headers = getAuthHeaders(options.headers);
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 && retry) {
        try {
            await refreshAccessToken();
            return fetchWithAuth(url, options, false);
        } catch (refreshError) {
            logError("Token refresh failed:", refreshError);

            // Trigger global authentication error handler
            if (globalAuthErrorHandler) {
                console.error("Authentication expired globally. Redirecting to login...");
                // Call the handler asynchronously to avoid blocking
                setTimeout(() => {
                    globalAuthErrorHandler(refreshError);
                }, 100);
            }

            throw refreshError;
        }
    }

    if (!response.ok) {
        logError(`API request failed: ${response.status} ${response.statusText}`, url);
    }

    return response;
}
